import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import {
  Checkbox,
  Form,
  FormField,
  FormProps,
  FormSchemaEntry,
  FormType,
  LoadingSpinner,
  ValidationErrors,
} from '@gik/ui';
import { translationKeys } from './i18n/en';
import { CalendarServiceTile, IWordpressServiceProductBundleOption } from '../Services';
import { useTranslation } from 'react-i18next';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { FormRenderProps } from 'react-final-form';
import { Mutator, FormApi } from 'final-form';
import { useServiceCategories, useServicesByEventTypeId } from '@gik/api/calendar/serviceProductBundle';
import { serviceModules } from '@gik/calendar';
import { CalendarServiceIds } from '@gik/calendar/models/CalendarEventTypes';
import { Analytics } from '@gik/core/utils';

export enum CalendarEventServicesFormFieldNames {
  Services = 'allowedServiceIds',
}

export interface ICalendarServicesFormValues {
  [CalendarEventServicesFormFieldNames.Services]?: number[];
}

export const calendarEventServicesFormSchema = (options: IWordpressServiceProductBundleOption[]): FormSchemaEntry[] => [
  {
    type: 'select-list',
    name: CalendarEventServicesFormFieldNames.Services,
    errorName: 'recipient type',
    required: true,
    requiredMessage: 'Please select at least one type',
    props: {
      className: 'gik-calendar-claim-type-select-list',
      multiple: true,
      options,
      render: (item: IWordpressServiceProductBundleOption, selected: boolean) => {
        return <CalendarServiceTile multiple horizontal selected={selected} image={item.image} label={item.label} />;
      },
    },
  },
];

export interface ICalendarEventServicesFormProps extends FormProps {
  eventTypeId: number;
  eventTypeName: string;
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
}

function _CalendarEventServicesForm(
  {
    className,
    eventTypeName,
    buttons,
    buttonsPortal,
    eventTypeId,
    onSubmit,
    ...otherProps
  }: ICalendarEventServicesFormProps,
  ref: React.MutableRefObject<FormType>
) {
  const bem = bemBlock('calendar-event-services-form');
  const { t } = useTranslation();

  // Local State
  const [options, setOptions] = React.useState<IWordpressServiceProductBundleOption[]>();

  // SWR
  const { data: serviceProductBundles } = useServiceCategories();
  const { data: matchingServices } = useServicesByEventTypeId(eventTypeId);

  console.log(
    'MATCHING SERVICES FOR ' + eventTypeId + ': ',
    matchingServices?.map(s => ({ id: s.id, slug: s.slug }))
  );

  // const [formValues, setFormValues] = React.useState<Partial<ICalendarServicesFormValues>>({});
  const [allSelected, setAllSelected] = React.useState<boolean>(false);
  // const { data: options } = useServiceProductBundles(eventTypeId);
  // const { data: eventTypes } = useCalendarEventTypes();

  const formId = 'CalendarServicesForm';
  const formApiRef = React.useRef<FormApi>();

  /**
   * Create a list of matching service categories when all data is ready
   */
  React.useEffect(() => {
    if (!serviceProductBundles || !matchingServices) return;

    const options: IWordpressServiceProductBundleOption[] = matchingServices.map(service => {
      const category = serviceProductBundles.find(item => item.id === service['service-category'][0]);

      return {
        label: service.acf.display_name || category.name,
        value: service.id,
        image: service.acf.taxonomy_svg_icon || category.acf.taxonomy_svg_icon,
      };
    });
    setOptions(options);
  }, [serviceProductBundles, matchingServices]);

  if (!options) return <LoadingSpinner />;

  const schema = calendarEventServicesFormSchema(options);

  function customValidateForm(values: ICalendarServicesFormValues): ValidationErrors {
    const errors: ValidationErrors = {};

    if (
      (values && !values[CalendarEventServicesFormFieldNames.Services]) ||
      values[CalendarEventServicesFormFieldNames.Services]?.length < 1
    ) {
      errors[CalendarEventServicesFormFieldNames.Services] = {
        message: t(translationKeys.ServiceRequired),
        type: 'singleRequired',
      };
    }

    return errors;
  }

  function renderTitle() {
    return (
      <span className="gik-calendar-modal-section-title">
        {t(translationKeys.ServiceLabel, { name: eventTypeName })}
      </span>
    );
  }

  function handleFormChange(values: ICalendarServicesFormValues) {
    if (values && values[CalendarEventServicesFormFieldNames.Services]?.length >= options.length) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }

  function fireSelectedServicesAnalytics(values: ICalendarServicesFormValues) {
    // TODO: move analytics to separate file
    // TODO: use in calendarServicesForm
    const matchingServiceIds = matchingServices.map(service => service.id);
    const selectedServiceIds = values[CalendarEventServicesFormFieldNames.Services];
    const services = matchingServices;

    const helpSelectedFields: { [key: string]: string } = {};
    const hasInPersonService = (serviceIds: number[]): boolean =>
      serviceIds.some(
        serviceId =>
          serviceId !== CalendarServiceIds.SeniorCarePhoneCall &&
          services.find(s => s.id === serviceId).acf.service_module === serviceModules.ProvideMyself
      );

    if (hasInPersonService(matchingServiceIds)) {
      helpSelectedFields.helpInPersonSelected = hasInPersonService(selectedServiceIds).toString();
    }

    const hasGiftCardService = (serviceIds: number[]): boolean =>
      serviceIds.some(
        serviceId =>
          [serviceModules.MealGiftCard, serviceModules.GenericGiftCard].indexOf(
            services.find(s => s.id === serviceId).acf.service_module
          ) >= 0
      );

    if (hasGiftCardService(matchingServiceIds)) {
      helpSelectedFields.helpGiftCardSelected = hasGiftCardService(selectedServiceIds).toString();
    }

    const hasOnlineService = (serviceIds: number[]): boolean =>
      serviceIds.some(
        serviceId => services.find(s => s.id === serviceId).acf.service_module === serviceModules.Grubhub
      );

    if (hasOnlineService(matchingServiceIds)) {
      helpSelectedFields.helpOrderOnlineSelected = hasOnlineService(selectedServiceIds).toString();
    }

    const hasPhoneCallService = (serviceIds: number[]): boolean =>
      serviceIds.some(serviceId => serviceId === CalendarServiceIds.SeniorCarePhoneCall);

    if (hasPhoneCallService(matchingServiceIds)) {
      helpSelectedFields.helpPhoneCallSelected = hasPhoneCallService(selectedServiceIds).toString();
    }

    Analytics.fireEvent(
      'EventCreateStarted',
      {
        stepCompleted: 'HelpType',
        ...helpSelectedFields,
      },
      _ => ['stepCompleted'],
      keys => keys.filter(key => key != 'stepCompleted')
    );
  }

  function handleFormSubmit(values: object, form: FormApi) {
    fireSelectedServicesAnalytics(values);
    onSubmit?.(values, form);
  }

  function handleAllChange(checked: boolean) {
    if (checked) {
      formApiRef.current.mutators.updateServices(options.map(item => item.value));
    } else {
      formApiRef.current.mutators.updateServices([]);
    }
  }

  const updateServices: Mutator = (args, state, utils) => {
    utils.changeValue(state, CalendarEventServicesFormFieldNames.Services, () => args[0]);
  };

  return (
    <div className={bem('wrapper', null, className)}>
      {renderTitle()}

      <Checkbox
        className={bem('checkAll')}
        label="All of them!"
        checked={allSelected}
        onValueChange={handleAllChange}
      />

      <Form
        className={bem(null, null, className)}
        schema={schema}
        vertical
        id={formId}
        ref={ref}
        validate={customValidateForm}
        onSubmit={handleFormSubmit}
        onChange={handleFormChange}
        mutators={{ updateServices }}
        render={form => {
          formApiRef.current = form.form;
          const _buttons = buttons ? buttons(form, formId) : null;

          return (
            <>
              <FormField
                center
                labelClassName={'gik-calendar-form-label'}
                name={CalendarEventServicesFormFieldNames.Services}
              />

              {renderPortal(_buttons, buttonsPortal)}
            </>
          );
        }}
        {...otherProps}
      />
    </div>
  );
}

export const CalendarServicesForm = React.forwardRef(_CalendarEventServicesForm);
